import React from 'react';
import * as ThemeUI from 'theme-ui';
import { WidgetProps } from '@rjsf/core';
import { FormattedMessage } from 'lib/intl';
import { OutlinedTextWidget } from 'components/form-elements/JsonForms/components/Text';
import { ErrorMessage } from 'components/form-elements/JsonForms/components/ErrorMessage';
import { LocaleKey } from 'translations';

/** Maximum number of words allowed in the "Type of Equipment Used" field. */
export const EQUIPMENT_TYPE_MAX_WORDS = 80;

/** Translation key for the "too long" validation message. */
export const EQUIPMENT_TYPE_MAX_WORDS_ERROR: LocaleKey = 'errors.equipmentType.maxWords';

/**
 * Counts the number of whitespace separated words in a value.
 * Empty / non-string values count as 0 words.
 */
export function countWords(value?: unknown): number {
  if (typeof value !== 'string') {
    return 0;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return 0;
  }

  return trimmed.split(/\s+/).length;
}

/** Whether a value exceeds the equipment type word limit. */
export function isEquipmentTypeOverLimit(value?: unknown): boolean {
  return countWords(value) > EQUIPMENT_TYPE_MAX_WORDS;
}

/**
 * Widget for the "Type of Equipment Used" (`equipmentType`) text field.
 *
 * Behaves like the regular text widget but adds an instant (per-keystroke)
 * validation that limits the field to {@link EQUIPMENT_TYPE_MAX_WORDS} words.
 * As soon as the user goes over the limit the input is marked as errored and an
 * inline message is shown - the user does not have to submit the form first.
 *
 * Submission is additionally blocked by the form-level `validate` callback in
 * `ReliningPage`, which surfaces the same error (via `FieldTemplate`) after a
 * submit attempt. To avoid showing the message twice, the inline message here
 * is suppressed once that form error is already present.
 */
export function EquipmentTypeWidget(props: WidgetProps) {
  const overLimit = isEquipmentTypeOverLimit(props.value);
  const alreadyReported = (props.rawErrors ?? []).includes(
    EQUIPMENT_TYPE_MAX_WORDS_ERROR,
  );

  /*
   * Feed the limit error into the underlying input so it is styled as errored
   * immediately, without waiting for a submit attempt to turn on live
   * validation.
   */
  const rawErrors = overLimit && !alreadyReported
    ? [...(props.rawErrors ?? []), EQUIPMENT_TYPE_MAX_WORDS_ERROR]
    : props.rawErrors;

  return (
    <React.Fragment>
      <OutlinedTextWidget {...props} rawErrors={rawErrors} />

      {overLimit && !alreadyReported && (
        <ThemeUI.Box mt={1}>
          <ErrorMessage>
            <FormattedMessage id={EQUIPMENT_TYPE_MAX_WORDS_ERROR} />
          </ErrorMessage>
        </ThemeUI.Box>
      )}
    </React.Fragment>
  );
}

